import update from "immutability-helper";

export const ACTION_TYPES = {
  ENABLE_EDIT_MODE: 'ENABLE_EDIT_MODE',
  DISCARD_CHANGES: 'DISCARD_CHANGES',
  DISABLE_EDIT_MODE: 'DISABLE_EDIT_MODE',
  ADD_COMMENT_PHASE: 'ADD_COMMENT_PHASE',
  ADD_DOCUSIGN_PHASE: 'ADD_DOCUSIGN_PHASE',
  MOVE_PHASE: 'MOVE_PHASE',
  ADD_NEW_CONNECTION: 'ADD_NEW_CONNECTION',
  DELETE_COMMENT_PHASE: 'DELETE_COMMENT_PHASE',
  UPDATE_DATA: 'UPDATE_DATA',
  DELETE_CONNECTION: 'DELETE_CONNECTION',
  ADD_PHASE_FORM: 'ADD_PHASE_FORM',
  ADD_PHASE_DATA_CAPTURE: 'ADD_PHASE_DATA_CAPTURE',
  ADD_PHASE_DATA_CAPTURE_V2: 'ADD_PHASE_DATA_CAPTURE_V2',
  ADD_PHASE_PRE_FILL_CONTEXT: 'ADD_PHASE_PRE_FILL_CONTEXT',
  UPDATE_PHASE_FORM: 'UPDATE_PHASE_FORM',
  UPDATE_PHASE_DATA_CAPTURE: 'UPDATE_PHASE_DATA_CAPTURE',
  UPDATE_PHASE_PRE_FILL_CONTEXT: 'UPDATE_PHASE_PRE_FILL_CONTEXT',
  REMOVE_PHASE_FORM: 'REMOVE_PHASE_FORM',
  REMOVE_PHASE_DATA_CAPTURE: 'REMOVE_PHASE_DATA_CAPTURE',
  REMOVE_PHASE_DATA_CAPTURE_V2: 'REMOVE_PHASE_DATA_CAPTURE_V2',
  REMOVE_PHASE_PRE_FILL_CONTEXT: 'REMOVE_PHASE_PRE_FILL_CONTEXT',
  UPDATE_PHASE_FORM_ORDER: 'UPDATE_PHASE_FORM_ORDER',
}

const findPreviousPhase = (phases, xLevel, yLevel, side) => {
  const fillPhaseIdx = phases.map(p => p.type).indexOf('fill')
  return phases.filter(p => p.y_level == yLevel && p.x_level <= xLevel)
    .reduce((acc, curVal) => curVal.x_level > acc.x_level ? curVal : acc, phases[fillPhaseIdx])
}


const getPreviousIds = (phases, xLevel, yLevel, side) => {
  const prevX = findPreviousPhase(phases, xLevel, yLevel, side);
  return [prevX.id];
}


// Returns the possible next phase based on the xLevel
const findPossibleNextPhase = (phases, xLevel, yLevel) => {
  const nextX = Math.min(...phases.filter(p => p.x_level > xLevel && p.y_level == yLevel).map(p => p.x_level));
  return phases.findIndex(p => p.x_level == nextX && p.y_level == yLevel)
}

const updatePreviousIdsAfterAdd = (phases, newPhase, didReplace) => {
  const approvePhaseIdx =  phases.findIndex(p => p.type == 'approval') // Only one should exist
  const fillPhaseId = phases.find(p => p.type == 'fill').id // Only one should exist

  let previousIdAssigned = false;
  let updatedPhases = [...phases].map(p => {
    const index = (p.previous || []).indexOf(newPhase.previous[0]);
    let updatedPrevious = [...(p.previous || [])]

    if (index !== -1 && (didReplace || newPhase.previous[0] != fillPhaseId)){
      updatedPrevious.splice(index, 1, newPhase.id);
      previousIdAssigned = true
    }
    
    return {...p, previous: updatedPrevious}
  })

  if (!previousIdAssigned) {
    const con = findPossibleNextPhase(updatedPhases, newPhase.x_level, newPhase.y_level);
    if (con != -1){
      updatedPhases[con].previous.push(newPhase.id);
    } else {
      updatedPhases[approvePhaseIdx].previous.push(newPhase.id);
    }
  }

  return updatedPhases
}

const updatePreviousIdsAfterDeletion = (phases, deletedPhase) => {
  const approvePhaseIdx =  phases.findIndex(p => p.type == 'approval') // Only one should exist
  const fillPhaseId = phases.find(p => p.type == 'fill').id // Only one should exist

  phases.forEach((p, idx) => {
    const linkedIdx = p.previous.indexOf(deletedPhase.id)
    if ( linkedIdx != -1 ){
      // Add the previous ids of deleted ones to the the phases, which contains the deleted in previous
      // Remove connection to the deleted & remove connection from fill to approve (unless they are the only phases)
      phases[idx].previous = [
        ...phases[idx].previous, 
        ...deletedPhase.previous
      ].filter(id => id != deletedPhase.id && !(id == fillPhaseId && idx == approvePhaseIdx && phases.length > 2));
    }
  })
  return phases;
}


const fixPositions = (tempState, id, xLevel = 0) => {

  const findConnected = (p, id) => {
    return p.filter(s => s.previous && s.previous.includes(id));
  }

  let pos = tempState.find(s => s.id == id)
  if (pos.x_level < xLevel){
    pos.x_level = xLevel
  }
  ++xLevel;

  findConnected(tempState, id).forEach(c => {
    fixPositions(tempState, c.id, xLevel);
  });
}

// Clearing empty x-levels. This prevents connectors from being unnecessarily long after removing all elements at x-level
const adjustXLevels = (phases) => {
  phases.sort((a, b) => a.x_level - b.x_level);
  
  let currentLevel = 0;
  return phases.map((item, index, sortedArr) => {
    if (index > 0 && sortedArr[index - 1].x_level < item.x_level) {
      currentLevel++;
    }
    return { ...item, x_level: currentLevel };
  });
};

const phaseReducer = (state, action) => {
  let deepCopy;
  let toIdx;

  switch (action.type){
    // Updates the complete reducer data (used for init)
    case ACTION_TYPES.UPDATE_DATA: 
      return {...state, data: action.payload.data}

    // Phase edit mode (node drag'n'drop)
    case ACTION_TYPES.ENABLE_EDIT_MODE: 
      deepCopy = JSON.parse(JSON.stringify(state.data));
      return {...state, pastData: deepCopy, editMode: true};

    // Discard current changes
    case ACTION_TYPES.DISCARD_CHANGES:
      return {...state, data: [...state.pastData], editMode: false};

    // Phase edit mode (node drag'n'drop)
    case ACTION_TYPES.DISABLE_EDIT_MODE:
      return {...state, editMode: false};

    // Adds comment phase in an empty space, creates new connections between previous / next node. 
    case ACTION_TYPES.ADD_COMMENT_PHASE: {
      const {xLevel, yLevel, side} = action.payload;
      deepCopy = JSON.parse(JSON.stringify(state));
      const current = deepCopy.data.find(p => p.x_level == xLevel && p.y_level == yLevel);
      let tempData = [...deepCopy.data];

      const newPhase = {
          id: "new_" + Math.round(Math.random()*1000000), // Random id, TBD: should check that won't match to an existing one
          previous: getPreviousIds(tempData, side == 'left' ? xLevel - 0.5 : xLevel, yLevel, side), 
          type: 'commenting', 
          x_level: xLevel, 
          y_level: yLevel,
          name: `${I18n.t('review')} ${Math.max(...tempData.map(p => parseInt((p.name || '0').split(I18n.t('review')).pop(), 10))) + 1}`
      }
      tempData = updatePreviousIdsAfterAdd(tempData, newPhase, !!current);
      tempData = [...tempData, newPhase]

      fixPositions(tempData, tempData.find(p => p.type == 'fill').id);
      return {...state, data: tempData};
    }

     // Adds docusign phase in an empty space, creates new connections between previous / next node. 
     case ACTION_TYPES.ADD_DOCUSIGN_PHASE: {
      const {xLevel, yLevel, side} = action.payload;
      deepCopy = JSON.parse(JSON.stringify(state));
      const current = deepCopy.data.find(p => p.x_level == xLevel && p.y_level == yLevel);
      let tempData = [...deepCopy.data];

      const newPhase = {
          id: "new_" + Math.round(Math.random()*1000000), // Random id, TBD: should check that won't match to an existing one
          previous: getPreviousIds(tempData, side == 'left' ? xLevel - 0.5 : xLevel, yLevel, side), 
          type: 'docusign', 
          x_level: xLevel, 
          y_level: yLevel,
          name: `${I18n.t('docusign')} ${Math.max(...tempData.map(p => parseInt((p.name || '0').split(I18n.t('docusign')).pop(), 10))) + 1}`
      }
      tempData = updatePreviousIdsAfterAdd(tempData, newPhase, !!current);
      tempData = [...tempData, newPhase]

      fixPositions(tempData, tempData.find(p => p.type == 'fill').id);
      return {...state, data: tempData};
    }
   
    case ACTION_TYPES.MOVE_PHASE: {
      const {id, xLevel, yLevel, side} = action.payload;
      deepCopy = JSON.parse(JSON.stringify(state.data));
      let target = deepCopy.find(p => p.id == id);
      target.x_level = xLevel;
      target.y_level = yLevel;
      return {...state, data: deepCopy }
    }
    // Delete a review or docusign phase, fixes positions
    case ACTION_TYPES.DELETE_COMMENT_PHASE:
      let tD = [...state.data];
      let deletedItem = tD.splice(action.payload.index, 1)[0];
      tD = updatePreviousIdsAfterDeletion(tD, deletedItem);

      fixPositions(tD, tD.find(p => p.type == 'fill').id);
      return {...state, data: adjustXLevels(tD)};

    // Adds a new connection between two nodes. Fixes positions if they overlap
    case ACTION_TYPES.ADD_NEW_CONNECTION:
      const {fromPhase, toPhase} = action.payload;
      deepCopy = JSON.parse(JSON.stringify(state))
      toIdx = deepCopy.data.findIndex(p => p.id == toPhase.id);
      deepCopy.data[toIdx].previous.push(fromPhase.id);
      fixPositions(deepCopy.data, deepCopy.data.find(p => p.type == 'fill').id);
      // Because docusign can be after the approval phase
      fixPositions(deepCopy.data, deepCopy.data.find(p => p.type == 'approval').id);
      return {...state, data: deepCopy.data}; 

    // Deletes connection, tries to fix positions (remove empty spaces)
    case ACTION_TYPES.DELETE_CONNECTION:
      const {fromPhaseId, toPhaseId} = action.payload;
      deepCopy = JSON.parse(JSON.stringify(state))
      toIdx = deepCopy.data.findIndex(p => p.id == toPhaseId);
      const removeIdx = deepCopy.data[toIdx].previous.indexOf(fromPhaseId);
      deepCopy.data[toIdx].previous.splice(removeIdx,1);
      // fixPositions(deepCopy.data, deepCopy.data.find(p => p.type == 'fill').id);
      return {...state, data: deepCopy.data}; 

    case ACTION_TYPES.ADD_PHASE_FORM: {
      const {newPhase, phaseId} = action.payload;

      let newState = update(state, {
        data: (phases) =>
          update(phases, {
            [phases.findIndex((p) => p.id == phaseId)]: {
              forms: (forms) => (forms || []).concat([newPhase]),
            },
          }),
      });

      return newState;
    }

    case ACTION_TYPES.ADD_PHASE_DATA_CAPTURE: {
      const {newPhase, phaseId} = action.payload;

      let newState = update(state, {
        data: (phases) =>
            update(phases, {
              [phases.findIndex((p) => p.id == phaseId)]: {
                data_capture: (data_capture) => (data_capture || []).concat([newPhase]),
              },
            }),
      });

      return newState;
    }

    case ACTION_TYPES.ADD_PHASE_DATA_CAPTURE_V2: {
      const {newPhase, phaseId} = action.payload;

      let newState = update(state, {
        data: (phases) =>
            update(phases, {
              [phases.findIndex((p) => p.id == phaseId)]: {
                form_capture: (form_capture) => (form_capture || []).concat([newPhase]),
              },
            }),
      });

      return newState;
    }

    case ACTION_TYPES.ADD_PHASE_PRE_FILL_CONTEXT: {
      const {newPhase, phaseId} = action.payload;

      let newState = update(state, {
        data: (phases) =>
            update(phases, {
              [phases.findIndex((p) => p.id == phaseId)]: {
                prefill_forms: (f) => (f || []).concat([newPhase]),
              },
            }),
      });

      return newState;
    }

    case ACTION_TYPES.UPDATE_PHASE_FORM: {
      const {newPhase, phaseId} = action.payload;

      let newState = update(state, {
        data: (phases) =>
          update(phases, {
            [phases.findIndex((p) => p.id == phaseId)]: {
              forms: (forms) => update(forms, {
                [forms.findIndex((f) => f.id == newPhase.id)]: {
                  $set: newPhase
                },
              }),
            },
          }),
      });

      return newState;
    }

    case ACTION_TYPES.UPDATE_PHASE_DATA_CAPTURE: {
      const {newPhase, phaseId} = action.payload;

      let newState = update(state, {
        data: (phases) =>
            update(phases, {
              [phases.findIndex((p) => p.id == phaseId)]: {
                data_capture: (data_capture) => update(data_capture, {
                  [data_capture.findIndex((f) => f.id == newPhase.id)]: {
                    $set: newPhase
                  },
                }),
              },
            }),
      });

      return newState;
    }

    case ACTION_TYPES.UPDATE_PHASE_PRE_FILL_CONTEXT: {
      const {newPhase, phaseId} = action.payload;

      let newState = update(state, {
        data: (phases) =>
            update(phases, {
              [phases.findIndex((p) => p.id == phaseId)]: {
                prefill_forms: (prefill_forms) => update(prefill_forms, {
                  [prefill_forms.findIndex((f) => f.id == newPhase.id)]: {
                    $set: newPhase
                  },
                }),
              },
            }),
      });

      return newState;
    }

    case ACTION_TYPES.UPDATE_PHASE_FORM_ORDER: {
      const {forms, phaseId} = action.payload;

      let newState = update(state, {
        data: (phases) =>
          update(phases, {
            [phases.findIndex((p) => p.id == phaseId)]: {
              forms: {
                $set: forms,
              },
            },
          }),
      });

      return newState;
    }

    case ACTION_TYPES.REMOVE_PHASE_FORM: {
      const {rowId, phaseId} = action.payload;

      let newState = update(state, {
        data: (phases) =>
          update(phases, {
            [phases.findIndex((p) => p.id == phaseId)]: {
              forms: (forms) =>
                update(forms, {
                  $splice: [[forms.findIndex((f) => f.id == rowId), 1]],
                }),
            },
          }),
      });

      return newState;
    }

    case ACTION_TYPES.REMOVE_PHASE_DATA_CAPTURE: {
      const {rowId, phaseId} = action.payload;

      let newState = update(state, {
        data: (phases) =>
            update(phases, {
              [phases.findIndex((p) => p.id == phaseId)]: {
                data_capture: (data_capture) =>
                    update(data_capture, {
                      $splice: [[data_capture.findIndex((f) => f.id == rowId), 1]],
                    }),
              },
            }),
      });

      return newState;
    }

    case ACTION_TYPES.REMOVE_PHASE_DATA_CAPTURE_V2: {
      const {rowId, phaseId} = action.payload;

      let newState = update(state, {
        data: (phases) =>
            update(phases, {
              [phases.findIndex((p) => p.id == phaseId)]: {
                form_capture: (form_capture) =>
                    update(form_capture, {
                      $splice: [[form_capture.findIndex((f) => f.id == rowId), 1]],
                    }),
              },
            }),
      });

      return newState;
    }

    case ACTION_TYPES.REMOVE_PHASE_PRE_FILL_CONTEXT: {
      const {rowId, phaseId} = action.payload;

      let newState = update(state, {
        data: (phases) =>
            update(phases, {
              [phases.findIndex((p) => p.id == phaseId)]: {
                prefill_forms: (prefill_forms) =>
                    update(prefill_forms, {
                      $splice: [[prefill_forms.findIndex((f) => f.id == rowId), 1]],
                    }),
              },
            }),
      });

      return newState;
    }

    default:
      throw new Error("Unknown reducer action type: " + action.type);
  }
}


export default phaseReducer;